import React, { useMemo, useState } from 'react';
import { Briefcase, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTasks } from '../../../../context/TasksContext';
import ScheduleJobModal from '../../../../components/scheduler/ScheduleJobModal';
import WorkOrderSheet from './WorkOrderSheet';
import JobCard from './JobCard';

/**
 * OneTimeJobsSection
 *
 * Customer Profile Overview surface for ad-hoc work — single visits that are
 * NOT part of a recurring program. Operational focus only: shows upcoming
 * and active one-time jobs, sorted by the next visit the CSR cares about.
 * Completed and cancelled jobs intentionally live in the Jobs tab (the
 * canonical historical record), not here.
 *
 * Reuses (no parallel implementation):
 *   - `ScheduleJobModal` — the canonical one-time job creator used by
 *     Header / Calendar slot-click / Pipeline / Quote.
 *   - `WorkOrderSheet`  — the technician completion interface used by JobsTab.
 *   - `JobCard`         — the shared presentational card.
 *   - `TasksContext`    — single live task stream powering both surfaces.
 *
 * Filtering contract:
 *   - `task.type === 'job'`
 *   - `task.isRecurring !== true` (recurring visits live under ProgramCard's
 *     Upcoming Visits disclosure, not here)
 *   - status is NOT 'completed' / 'cancelled' / 'archived' (history → Jobs tab)
 *
 * Sort: nearest upcoming first. `scheduledStart` ASC, then unscheduled
 * (status='approved' with no date) below scheduled items but still visible
 * because they remain actionable.
 *
 * V1 list cap: 5. When more exist, a "View all in Jobs tab →" link surfaces.
 */
const OneTimeJobsSection = ({ client }) => {
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const leadId = client?.leadId || (!client?.clientId ? client?.id : null);

  // ── Filter + sort ───────────────────────────────────────────────────────
  const TERMINAL = new Set(['completed', 'cancelled', 'archived']);
  const allOneTime = useMemo(() => {
    if (!leadId) return [];
    return tasks.filter((t) =>
      t.type === 'job'
      && t.leadId === leadId
      && t.isRecurring !== true
      && !TERMINAL.has(t.status || '')
    );
  }, [tasks, leadId]);

  const sorted = useMemo(() => {
    const scheduledFirst = (a, b) => {
      // Unscheduled (no scheduledStart) sort BELOW scheduled jobs but
      // stay visible — they're "needs scheduling" work, not dead weight.
      const aHas = !!a.scheduledStart;
      const bHas = !!b.scheduledStart;
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      if (!aHas && !bHas) {
        // Both unscheduled — fall back to createdAt (oldest first, treat as backlog age)
        const aC = a.createdAt?.toMillis?.() ?? 0;
        const bC = b.createdAt?.toMillis?.() ?? 0;
        return aC - bC;
      }
      // Both scheduled — nearest upcoming first
      return a.scheduledStart.localeCompare(b.scheduledStart);
    };
    return [...allOneTime].sort(scheduledFirst);
  }, [allOneTime]);

  const LIST_CAP = 5;
  const visible = sorted.slice(0, LIST_CAP);
  const overflowCount = Math.max(0, sorted.length - LIST_CAP);

  // ── Handlers ────────────────────────────────────────────────────────────
  const openJob = (task) => {
    setSelectedTaskId(task.id);
    setSheetOpen(true);
  };

  const closeJob = () => {
    setSheetOpen(false);
    setTimeout(() => setSelectedTaskId(null), 300);
  };

  const selectedTask = selectedTaskId
    ? (allOneTime.find((t) => t.id === selectedTaskId) || null)
    : null;

  const handleViewAll = () => {
    navigate(`/clients/${client?.id}?tab=jobs`);
  };

  return (
    <>
      <ScheduleJobModal
        lead={client}
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        forceOneTime
      />
      <WorkOrderSheet
        task={selectedTask}
        client={client}
        open={sheetOpen}
        onClose={closeJob}
        onCompleted={closeJob}
      />

      <section
        className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4"
        data-testid="one-time-jobs-section"
      >
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">One-Time Jobs</h2>
            {sorted.length > 0 && (
              <span className="text-xs text-gray-400" data-testid="one-time-jobs-count">
                ({sorted.length})
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-50 transition-colors"
            data-testid="one-time-jobs-add-btn"
          >
            <Plus className="h-3.5 w-3.5" />
            Schedule Job
          </button>
        </header>

        {sorted.length === 0 ? (
          <EmptyOneTimeJobsCard onSchedule={() => setScheduleOpen(true)} />
        ) : (
          <div className="space-y-3" data-testid="one-time-jobs-list">
            {visible.map((task) => (
              <JobCard key={task.id} task={task} onClick={openJob} />
            ))}
            {overflowCount > 0 && (
              <button
                type="button"
                onClick={handleViewAll}
                className="w-full text-center text-xs font-semibold text-gray-500 hover:text-gray-700 py-2 transition-colors"
                data-testid="one-time-jobs-view-all"
              >
                View all in Jobs tab → ({overflowCount} more)
              </button>
            )}
          </div>
        )}
      </section>
    </>
  );
};

export default OneTimeJobsSection;

// ─── Empty-state card (mirrors the Programs empty card style) ─────────────
function EmptyOneTimeJobsCard({ onSchedule }) {
  return (
    <div
      className="bg-white rounded-md border border-dashed border-gray-300 p-4"
      data-testid="one-time-jobs-empty-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="h-4 w-4 text-gray-300" />
        <span
          className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 text-gray-500 rounded-full px-2.5 py-0.5"
          data-testid="one-time-jobs-empty-pill"
        >
          None scheduled
        </span>
      </div>
      <p className="text-xs text-gray-500 leading-snug mb-3">
        Schedule an ad-hoc visit that doesn&apos;t belong to this customer&apos;s
        recurring program — for inspections, one-off treatments, or callbacks.
      </p>
      <button
        type="button"
        onClick={onSchedule}
        className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 transition-colors"
        data-testid="one-time-jobs-empty-cta"
      >
        <Plus className="h-3.5 w-3.5" />
        Schedule Job
      </button>
    </div>
  );
}
